/**
 * 企业组织岗位列表（用于成员筛选等；独立团队无岗位概念时不启用）。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgMembersApi } from '@/services/api/org-members';
import type { CreateEnterprisePositionDto, UpdateEnterprisePositionDto } from '@/types/org';

export const orgMemberPositionsKeys = {
  all: ['org-member-positions'] as const,
  list: (orgId: string) => [...orgMemberPositionsKeys.all, orgId] as const,
};

export function useOrgMemberPositions(orgId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: orgMemberPositionsKeys.list(orgId ?? ''),
    queryFn: () => orgMembersApi.listPositions(orgId!),
    enabled: enabled && !!orgId,
    staleTime: 1000 * 60 * 5,
  });
}

function useInvalidateOrgMemberPositions(orgId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: orgMemberPositionsKeys.list(orgId) });
  };
}

export function useCreateOrgMemberPositionMutation(orgId: string) {
  const invalidatePositions = useInvalidateOrgMemberPositions(orgId);

  return useMutation({
    mutationFn: (data: CreateEnterprisePositionDto) => orgMembersApi.createPosition(orgId, data),
    onSuccess: invalidatePositions,
  });
}

export function useUpdateOrgMemberPositionMutation(orgId: string) {
  const invalidatePositions = useInvalidateOrgMemberPositions(orgId);

  return useMutation({
    mutationFn: ({ positionId, data }: { positionId: string; data: UpdateEnterprisePositionDto }) =>
      orgMembersApi.updatePosition(orgId, positionId, data),
    onSuccess: invalidatePositions,
  });
}

export function useDeleteOrgMemberPositionMutation(orgId: string) {
  const invalidatePositions = useInvalidateOrgMemberPositions(orgId);

  return useMutation({
    mutationFn: (positionId: string) => orgMembersApi.deletePosition(orgId, positionId),
    onSuccess: invalidatePositions,
  });
}
